import { Navigate, Outlet, useLocation } from 'react-router';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { FullPageLoader } from '@/components/ui/FullPageLoader';
import { useCurrentUser, useIsRestoringSession } from '@/features/auth/useAuth';
import { useSessionStore } from '@/stores/session';

import styles from './guards.module.css';
import { routes } from './routes';

/**
 * Blocks a route until the visitor is known to be signed in.
 *
 * While the stored token is still being verified it renders a loader rather
 * than redirecting, otherwise every hard refresh would bounce the user through
 * the login screen.
 */
export function RequireAuth() {
    const token = useSessionStore((state) => state.token);
    const isRestoring = useIsRestoringSession();
    const { data: user, isPending, isError, refetch } = useCurrentUser();
    const location = useLocation();

    // Checked before the loading state, and this order matters. When a stored
    // token turns out to be invalid the API client clears the session, which
    // disables the /auth/me query — and a disabled TanStack query reports
    // `isPending` forever. Waiting on `isPending` first would leave the user
    // staring at a loading screen instead of the login form.
    if (token === null) {
        // `from` lets the login screen return the user to where they were
        // heading instead of dumping them on the dashboard.
        return <Navigate to={routes.login} state={{ from: location }} replace />;
    }

    if (isRestoring || isPending) {
        return <FullPageLoader label="Even je gegevens ophalen…" />;
    }

    /*
     * A failed profile request is not a signed-out user.
     *
     * This used to fall through to the login screen whenever `user` was
     * undefined, which quietly conflated three different things: an expired
     * session, a rate-limited request, and the API being briefly unreachable.
     * Only the first of those means "sign in again" — and it is already handled
     * above, because a 401 clears the token and the `token === null` branch
     * catches it.
     *
     * The other two produced the worst possible behaviour: the user was thrown
     * onto the login form while holding a perfectly valid token, so signing in
     * again "fixed" it and nothing explained what had happened.
     */
    if (isError) {
        return (
            /*
             * The heading is not decoration here, it is the only thing that says
             * where the reader is.
             *
             * This branch replaces the *entire* application — the guard sits above
             * the layout, so there is no shell, no navigation and no landmark left.
             * It used to open with the alert, which put the failure's own sentence
             * in a `<strong>` and left the document with no `<h1>` at all: a
             * screen-reader user got an unlabelled screen and heading navigation
             * found nothing. `e2e/layout.spec.ts`'s `settled()` waits for
             * `h1, [role="alert"]` and this branch had neither, so a transient
             * `/auth/me` failure cost fifteen seconds of timeout and a forty-seven
             * second test failure that named none of this.
             *
             * The sentence moved from the alert's title into the heading rather
             * than being written twice, which is also the shape `ErrorBoundary`
             * next door already uses.
             *
             * No `role="alert"`, deliberately. This is the initial content of the
             * screen the reader lands on, not something that arrives while they are
             * reading, and a live region inserted together with its text is not
             * dependably announced — it would be a fix nobody here could verify.
             * `ErrorBoundary` does carry the role, and the difference is real: it
             * takes over a page mid-session, after a crash, so there is something
             * to interrupt. `check:failure-titles` carries the full argument.
             */
            <div className={styles.failure}>
                <h1 className={styles.title}>We konden je gegevens niet ophalen</h1>

                <Alert tone="danger">
                    Je bent nog gewoon ingelogd. Waarschijnlijk was de verbinding even weg.
                </Alert>

                {/*
                 * Retry is the only way off this screen, and that is a known gap
                 * rather than an oversight. There is no shell to navigate with, and
                 * a "log out" button is exactly what this branch was written to
                 * avoid: a failed profile request is not a signed-out user, and
                 * offering to clear a valid session over a dropped connection is
                 * the bug documented above. If `/auth/me` keeps failing the reader
                 * is left with the browser's own back button and the address bar.
                 * Recorded, not fixed: what to offer instead is a product decision.
                 */}
                <Button
                    onClick={() => {
                        void refetch();
                    }}
                >
                    Opnieuw proberen
                </Button>
            </div>
        );
    }

    if (user === undefined) {
        return <Navigate to={routes.login} state={{ from: location }} replace />;
    }

    return <Outlet />;
}

/**
 * Keeps a signed-in user out of the login and registration screens.
 */
export function RequireGuest() {
    const token = useSessionStore((state) => state.token);
    const { data: user, isPending } = useCurrentUser();

    // No token at all: show the form immediately rather than flashing a loader.
    if (token !== null && isPending) {
        return <FullPageLoader label="Even geduld…" />;
    }

    if (user !== undefined) {
        return <Navigate to={routes.dashboard} replace />;
    }

    return <Outlet />;
}

/**
 * Sends a user who has not finished the intake to the guided flow.
 *
 * Onboarding produces the profile, equipment and goals that the recommendation
 * engine needs; without them the dashboard would have nothing truthful to say.
 */
export function RequireOnboarding() {
    const { data: user } = useCurrentUser();

    if (user !== undefined && user.profile?.onboarding.completed === false) {
        return <Navigate to={routes.onboarding} replace />;
    }

    return <Outlet />;
}
